import { ThemeComponent } from '@/theme/ThemeComponent';
import { Component, Prop, Ref } from '@eastsideco/salvo-ts';
import { cart } from '@/theme/Cart';
import { QuantitySelector } from '../QuantitySelector/QuantitySelector';

@Component
export class CartItem extends ThemeComponent {
    @Prop item!: Shopify.Liquid.LineItem;

    @Ref({ required: false }) quantitySelector!: QuantitySelector[];
    @Ref({ required: false }) removeItemButton!: HTMLButtonElement[];
    @Ref({ required: false }) errorMessage!: HTMLElement;

    private errorMsg: string = '';
    private domRoot: HTMLElement | null = null;

    init() {
        // Check if cart contains a gift card
        const hasGiftCard = cart.items.some((item) => item.handle === 'gift-card');
        const checkoutButton = document.querySelector('.cart__checkout-button') as HTMLElement;

        if (checkoutButton && hasGiftCard) {
            checkoutButton.classList.remove('button--disabled');
        }

        this.domRoot = this.getDomRoot();
        this.attachEventListeners();

        // Global delegated fallback for remove buttons (handles hoisted DOM)
        this.$listen(window, 'click', (event) => {
            const target = event.target as HTMLElement | null;
            if (!target) {
                return;
            }
            const button = target.closest('[data-ref="removeItemButton"]') as HTMLButtonElement | null;
            if (!button) {
                return;
            }
            const lineItemId = Number(button.dataset.lineItemId || '');
            const variantId = Number(button.dataset.variantId || '');
            const matchesLine = lineItemId === this.item.id;
            const matchesVariant = variantId === this.item.variant_id;
            if (!matchesLine && !matchesVariant) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            this.handleQuantity(0).catch(() => {
                // Silently handle errors
            });
        });

        // Re-attach listeners after cart updates (when DOM is replaced)
        document.addEventListener('cartChanged', () => {
            setTimeout(() => {
                this.domRoot = this.getDomRoot();
                this.attachEventListeners();
            }, 100);
        });
    }

    private getDomRoot(): HTMLElement | null {
        // Try to find the inner div first
        const innerRoot = this.querySelector('.cart-item__item-inner') as HTMLElement | null;
        if (innerRoot) {
            return innerRoot;
        }

        // Try to find the <tr> element (may be hoisted out of custom element)
        const variantId = this.item.id;
        const table = this.closest('table');
        if (table) {
            const rows = table.querySelectorAll('tr[item]');
            for (const row of rows) {
                try {
                    const itemAttr = row.getAttribute('item');
                    if (itemAttr) {
                        const parsedItem = JSON.parse(itemAttr) as Shopify.Liquid.LineItem;
                        if (parsedItem.id === variantId) {
                            return row as HTMLElement;
                        }
                    }
                } catch {
                    // Ignore parse errors
                }
            }
        }

        // Fallback: check siblings
        const neighbours: (Element | null)[] = [this.nextElementSibling, this.previousElementSibling];
        for (const neighbour of neighbours) {
            if (neighbour instanceof HTMLElement && neighbour.classList.contains('cart-item')) {
                try {
                    const itemAttr = neighbour.getAttribute('item');
                    if (itemAttr) {
                        const parsedItem = JSON.parse(itemAttr) as Shopify.Liquid.LineItem;
                        if (parsedItem.id === variantId) {
                            return neighbour;
                        }
                    }
                } catch {
                    // Ignore parse errors
                }
            }
        }

        return null;
    }

    private attachEventListeners(attempt: number = 0) {
        const root = this.getDomRoot();
        if (!root) {
            if (attempt < 5) {
                setTimeout(() => this.attachEventListeners(attempt + 1), 100);
            }
            return;
        }

        // Find remove button relative to the actual DOM root
        let removeButtons = Array.from(
            root.querySelectorAll('[data-ref="removeItemButton"]') as NodeListOf<HTMLButtonElement>
        );

        if (removeButtons.length === 0) {
            const lineSelector = `[data-line-item-id="${this.item.id}"]`;
            const variantSelector = `[data-variant-id="${this.item.variant_id}"]`;
            removeButtons = Array.from(document.querySelectorAll(
                `${lineSelector}, ${variantSelector}`
            )) as HTMLButtonElement[];
        }

        // Update refs
        this.removeItemButton = removeButtons as unknown as HTMLButtonElement[];

        // Attach click listeners
        for (const button of removeButtons) {
            this.$listen(button, 'click', () => this.handleQuantity(0));
        }

        // Quantity selectors should still work via @Ref since they're components
        if (this.quantitySelector) {
            for (const selector of this.quantitySelector) {
                this.$subscribe(selector.onChange, (quantity) => {
                    this.handleQuantity(quantity).catch(() => {
                        // Silently handle errors
                    });
                });
            }
        }
    }

    async handleQuantity(quantity: number) {
        // Find matching cart item
        let matchingItem: (typeof cart.items)[number] | null = null;
        const targetLineItemId = this.item.id;
        const targetVariantId = this.item.variant_id;

        for (const item of cart.items) {
            if (item.id === targetLineItemId || item.variantId === targetVariantId) {
                matchingItem = item;
                break;
            }
        }
        if (matchingItem === null) {
            return;
        }

        this.toggleGlobalSpinner(true);
        try {
            if (quantity === 0) {
                // Remove item
                await cart.removeItem(matchingItem);
            } else {
                // Change item
                await cart.changeItem(matchingItem, quantity, null);
            }

            // Clear error if update was successful
            this.errorMsg = '';
            this.hideErrorMessage();

        } catch (event: unknown) {
            const { message } = event as Error;
            this.errorMsg = `${message ? message.split('.')[0] : ''}`;
            this.showErrorMessage();
        } finally {
            this.toggleGlobalSpinner(false);
        }

        // Dispatch event for other components to listen to
        document.dispatchEvent(new Event('cartChanged'));
    }

    private toggleGlobalSpinner(isActive: boolean) {
        const spinner = document.querySelector('[data-ref="updatingCart"]') as HTMLElement | null;
        if (!spinner) {
            return;
        }
        spinner.classList.toggle('loading-spinner--active', isActive);
    }

    showErrorMessage() {
        const cartWrapper = document.querySelector('.cart-error');
        if (cartWrapper && this.errorMsg) {
            cartWrapper.textContent = this.errorMsg;
            cartWrapper.classList.add('show');
        }
    }

    hideErrorMessage() {
        const cartWrapper = document.querySelector('.cart-error');
        if (cartWrapper) {
            cartWrapper.textContent = '';
            cartWrapper.classList.remove('show');
        }
    }


}
